/**
 * AI Recipe Service
 *
 * Frontend service for generating Beast Feast recipes using AI.
 * Communicates with Firebase Cloud Function `generateBeastFeastRecipe`.
 *
 * Features:
 * - AI-powered recipe name and description generation
 * - Rate limit tracking (5 generations per campaign per day)
 * - Toast notifications for user feedback
 * - Graceful error handling with fallback to manual entry
 *
 * @see https://github.com/svivasgo/chag-innovations/issues/106
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import {
  trackAIRecipeRequested,
  trackAIRecipeSuccess,
  trackAIRecipeFailed,
  trackAICreditsDepleted
} from './analyticsService';

/**
 * Generate a Beast Feast recipe using AI
 *
 * @param {Object} params - Generation parameters
 * @param {Array} params.ingredients - Array of {name, source, flavors} objects
 * @param {number} params.mealRating - The meal rating (1+)
 * @param {string} params.campaignId - Campaign ID (for rate limiting)
 * @param {string} params.userId - User ID (for tracking)
 * @returns {Promise<Object>} {success, name, description, flavorProfile, creditsRemaining, cached, error}
 */
export const generateRecipeWithAI = async ({
  ingredients,
  mealRating,
  campaignId,
  userId
}) => {
  // Validate inputs
  if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) {
    return {
      success: false,
      error: 'No ingredients provided. Please add ingredients before generating a recipe.'
    };
  }

  if (typeof mealRating !== 'number' || mealRating < 1) {
    return {
      success: false,
      error: 'Invalid meal rating. Must be at least 1.'
    };
  }

  if (!campaignId) {
    return {
      success: false,
      error: 'Campaign ID is required for AI generation.'
    };
  }

  // Show loading toast
  const loadingToastId = toast.loading('✨ Generating recipe with AI...');

  // Track recipe request in analytics
  trackAIRecipeRequested(ingredients.length);

  try {
    // Call Cloud Function
    const functions = getFunctions();
    const generateRecipe = httpsCallable(functions, 'generateBeastFeastRecipe');

    const result = await generateRecipe({
      ingredients: ingredients.map(ing => ({
        name: ing.name,
        source: ing.source || 'Unknown Beast',
        flavors: ing.flavors || []
      })),
      mealRating,
      campaignId,
      userId
    });

    const data = result.data;

    // Track success in analytics
    trackAIRecipeSuccess(mealRating, data.cached || false);

    // Success!
    toast.success(
      data.cached
        ? '✨ Recipe retrieved from your cookbook!'
        : '✨ Recipe generated successfully!',
      { id: loadingToastId }
    );

    return {
      success: true,
      name: data.name,
      description: data.description,
      flavorProfile: data.flavorProfile,
      creditsRemaining: data.creditsRemaining,
      cached: data.cached || false
    };

  } catch (error) {
    console.error('AI recipe generation error:', error);

    // Parse error message
    let errorMessage = 'Failed to generate recipe. Please try entering details manually.';
    let creditsRemaining = null;
    let errorType = 'unknown';

    if (error.code === 'resource-exhausted') {
      errorMessage = 'Daily AI generation limit reached. Try again tomorrow or enter recipe details manually.';
      creditsRemaining = 0;
      errorType = 'rate_limit';
      trackAICreditsDepleted('recipe');
    } else if (error.code === 'unauthenticated') {
      errorMessage = 'You must be logged in to use AI features.';
      errorType = 'unauthenticated';
    } else if (error.code === 'invalid-argument') {
      errorMessage = error.message || errorMessage;
      errorType = 'invalid_input';
    } else if (error.code === 'deadline-exceeded' || error.code === 'unavailable') {
      errorMessage = 'AI service is temporarily unavailable. Please try again in a moment.';
      errorType = 'timeout';
    }

    // Track failure in analytics
    trackAIRecipeFailed(errorType);

    // Show error toast
    toast.error(errorMessage, { id: loadingToastId, duration: 5000 });

    return {
      success: false,
      error: errorMessage,
      creditsRemaining,
      code: error.code
    };
  }
};

/**
 * Check how many AI generations are remaining for a campaign today
 *
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<number>} Number of credits remaining (0-5)
 */
export const checkAICreditsRemaining = async (campaignId) => {
  try {
    const functions = getFunctions();
    const checkCredits = httpsCallable(functions, 'checkAICredits');

    const result = await checkCredits({ campaignId });

    return result.data.creditsRemaining || 0;

  } catch (error) {
    console.error('Failed to check AI credits:', error);

    // Return null to indicate we couldn't check
    // (UI should still allow user to try generation)
    return null;
  }
};

/**
 * Format credits remaining message for UI display
 *
 * @param {number|null} creditsRemaining - Number of credits (0-5) or null if unknown
 * @returns {string} User-friendly message
 */
export const formatCreditsMessage = (creditsRemaining) => {
  if (creditsRemaining === null || creditsRemaining === undefined) {
    return 'AI generations available';
  }

  if (creditsRemaining === 0) {
    return 'No AI generations remaining today';
  }

  if (creditsRemaining === 1) {
    return '1 AI generation remaining today';
  }

  return `${creditsRemaining} AI generations remaining today`;
};

/**
 * Determine if AI button should be enabled
 *
 * @param {number|null} creditsRemaining - Number of credits or null
 * @param {boolean} isGenerating - Whether generation is in progress
 * @returns {boolean} True if button should be enabled
 */
export const isAIGenerationAvailable = (creditsRemaining, isGenerating) => {
  if (isGenerating) return false;
  if (creditsRemaining === null) return true; // Unknown, let user try
  return creditsRemaining > 0;
};
